import { useState, useSyncExternalStore } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { AuthProvider, ProtectedRoute } from '@podus/auth';
import { authConfig } from './auth-config';
import { siteContentVersion } from './lib/siteContent';
import Preloader from '@components/preloader';
import { ToastProvider } from '@components/Toast';
import CookieConsent from '@components/landing/CookieConsent';
import Home from './pages/Home';
import OurSolutions from './pages/OurSolutions';
import About from './pages/About';
import FocusArea from './pages/FocusArea';
import Innovation from './pages/Innovation';
import Projects from './pages/Projects';
import ProjectDetail from './pages/Projects/Detail';
import ResourcesBlog from './pages/Resources/Blog';
import BlogArticle from './pages/Resources/BlogArticle';
import Publications from './pages/Resources/Publications';
import Infographics from './pages/Resources/Infographics';
import Reports from './pages/Resources/Reports';
import CourtRanking from './pages/Resources/CourtRanking';
import Stories from './pages/Resources/Stories';
import Story from './pages/Resources/Story';
import Gallery from './pages/Resources/Gallery';
import Help from './pages/Help';
import NotFound from './pages/NotFound';

/** Set once the intro has played, so returning visitors go straight to the page. */
export const INTRO_SEEN_KEY = 'cg-intro-seen';

/**
 * Whether this page load plays the intro. Resolved synchronously at import time —
 * before any component mounts — because Hero and Navbar read `__cgPreloading`
 * from their own mount effects to decide whether to wait for the hand-off event.
 * Leaving it unset (or stale) is what previously stranded the hero mid-reveal.
 */
const shouldPlayIntro = (() => {
    if (typeof window === 'undefined') return false;
    try {
        return !window.localStorage.getItem(INTRO_SEEN_KEY);
    } catch {
        // Storage blocked (private mode, cookie policy) — play it rather than fail.
        return true;
    }
})();

if (typeof window !== 'undefined') window.__cgPreloading = shouldPlayIntro;

export function App() {
    // The content pools are read during render. When the CMS answers after first
    // paint — a slow connection, a cold API — this re-renders the tree so every
    // page reads the real content instead of the bundled stand-ins it started on.
    useSyncExternalStore(siteContentVersion.subscribe, siteContentVersion.get);

    // First visit only. Repeat visits — and every in-app navigation back to Home —
    // render the page directly, with no loader and no waiting on the hand-off.
    const [isAppLoading, setIsAppLoading] = useState(shouldPlayIntro);

    const handleLoadingComplete = () => {
        setIsAppLoading(false);
    };

    return (
        <ToastProvider>
            <BrowserRouter>
                {isAppLoading && <Preloader onLoadingComplete={handleLoadingComplete} />}
                <AuthProvider config={authConfig}>
                    <Routes>
                        {/* Landing page */}
                        <Route index element={<Home />} />

                        {/* Our Solutions detail page */}
                        <Route path="our-solutions" element={<OurSolutions />} />

                        {/* About Us page */}
                        <Route path="about" element={<About />} />

                        {/* Focus Area pages — one template, slug-driven content */}
                        <Route path="focus-areas/:slug" element={<FocusArea />} />

                        {/* Innovation (product) pages — one template, slug-driven content */}
                        <Route path="innovations/:slug" element={<Innovation />} />

                        {/* Resources */}
                        <Route path="projects" element={<Projects />} />
                        <Route path="projects/:slug" element={<ProjectDetail />} />

                        <Route path="resources/blog" element={<ResourcesBlog />} />
                        <Route path="resources/blog/:slug" element={<BlogArticle />} />
                        <Route path="resources/publications" element={<Publications />} />
                        <Route path="resources/infographics" element={<Infographics />} />
                        <Route path="resources/reports" element={<Reports />} />
                        <Route path="resources/stories" element={<Stories />} />
                        <Route path="resources/stories/:slug" element={<Story />} />
                        <Route path="resources/gallery" element={<Gallery />} />
                        <Route path="resources/court-ranking" element={<CourtRanking />} />

                        {/* Protected dashboard routes */}
                        <Route element={<ProtectedRoute />}>{/* Add protected routes here */}</Route>

                        {/* 404 */}
                        <Route path="help" element={<Help />} />

                        <Route path="*" element={<NotFound />} />
                    </Routes>
                    <CookieConsent />
                </AuthProvider>
            </BrowserRouter>
        </ToastProvider>
    );
}
